import { useEffect, useState, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Debounced fabric fetcher. Returns items + total + loading.
 * params: { source, category, search, book, is_new, limit }
 * Triggers on any param change (with a small debounce when search changes).
 */
export function useFabrics(params, debounceMs = 250) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  // Serialize params to a stable key so the hook re-runs on real changes only
  const key = JSON.stringify(params);

  useEffect(() => {
    const myReqId = ++reqIdRef.current;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const query = {};
        Object.entries(params || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") query[k] = v;
        });
        const res = await axios.get(`${API}/fabrics`, { params: query });
        if (reqIdRef.current === myReqId) {
          setItems(res.data?.items || []);
          setTotal(res.data?.total || 0);
        }
      } catch (e) {
        if (reqIdRef.current === myReqId) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (reqIdRef.current === myReqId) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, debounceMs]);

  return { items, total, loading };
}

/**
 * Fetches button catalog from API (debounced).
 */
export function useButtons(params, debounceMs = 200) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);
  const key = JSON.stringify(params);

  useEffect(() => {
    const myReqId = ++reqIdRef.current;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const query = {};
        Object.entries(params || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") query[k] = v;
        });
        const res = await axios.get(`${API}/buttons`, { params: query });
        if (reqIdRef.current === myReqId) {
          setItems(res.data?.items || []);
          setTotal(res.data?.total || 0);
        }
      } catch (e) {
        if (reqIdRef.current === myReqId) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (reqIdRef.current === myReqId) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, debounceMs]);

  return { items, total, loading };
}

/**
 * One-shot fabric stats fetcher.
 */
export function useFabricStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/fabrics/stats`);
        if (active) setStats(res.data);
      } catch (e) {
        if (active) setStats(null);
      }
    })();
    return () => {
      active = false;
    };
  }, []);
  return stats;
}
